import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Conversation, ConversationQueueCounts } from '../../../types/conversation';
import {
  CHANNEL_TYPE_LABELS,
  CONVERSATION_STATUS_LABELS,
  ConversationStatus,
  SupportMessageType,
} from '../../../types/atendimentoEnums';
import { createAllView, WorkspaceView } from '../../../types/workspaceView';
import { WorkspaceThemeClasses } from './workspaceTheme';
import { WorkspaceQueueScope } from './useWorkspaceConversations';
import { WorkspaceViewSelector } from './WorkspaceViewSelector';
import { WorkspaceInboxFilter, WorkspaceInboxFilterValue } from './WorkspaceInboxFilter';
import { Inbox } from '../../../types/inbox';
import { ChannelTypeIcon } from '../ChannelTypeIcon';
import { ClassificationBadges } from './ClassificationBadges';
import { resolveClassificationBadges } from '../../../utils/classificationBadgeDisplay';
import { formatQueuePreview, formatUnreadBadge } from '../../../utils/conversationQueuePreview';

const EMPTY_QUEUE_COUNTS: ConversationQueueCounts = {
  active: 0,
  captured: 0,
  resolved: 0,
  all: 0,
  activeUnread: 0,
};

interface WorkspaceQueueProps {
  theme: WorkspaceThemeClasses;
  conversations: Conversation[];
  selectedId: string | null;
  loading: boolean;
  accessibleInboxes: Inbox[];
  inboxFilter: WorkspaceInboxFilterValue;
  queueScope: WorkspaceQueueScope;
  queueCounts?: ConversationQueueCounts;
  activeView: WorkspaceView;
  compact?: boolean;
  onSelect: (id: string) => void;
  onInboxFilterChange: (value: WorkspaceInboxFilterValue) => void;
  onQueueScopeChange: (scope: WorkspaceQueueScope) => void;
  onViewChange: (view: WorkspaceView) => void;
}

const formatRelativeTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime()))
  {
    return '';
  }

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);

  if (minutes < 1)
  {
    return 'agora';
  }

  if (minutes < 60)
  {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24)
  {
    return `${hours} h`;
  }

  return date.toLocaleDateString('pt-BR');
};

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0)
  {
    return '?';
  }

  if (parts.length === 1)
  {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarColor = (name: string): string => {
  const palette = ['#2563eb', '#1d4ed8', '#3b82f6', '#6366f1', '#0ea5e9', '#1e40af'];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1)
  {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return palette[Math.abs(hash) % palette.length];
};

const statusTone = (status: ConversationStatus): string => {
  if (status === ConversationStatus.WaitingForOperator || status === ConversationStatus.Pending)
  {
    return 'bg-amber-100 text-amber-800';
  }

  if (status === ConversationStatus.InProgress)
  {
    return 'bg-blue-100 text-blue-800';
  }

  if (status === ConversationStatus.Resolved)
  {
    return 'bg-gray-200 text-gray-700';
  }

  if (status === ConversationStatus.Captured)
  {
    return 'bg-purple-100 text-purple-800';
  }

  return 'bg-emerald-100 text-emerald-800';
};

const QUEUE_OPTIONS: Array<{
  value: WorkspaceQueueScope;
  label: string;
  hint: string;
  separated?: boolean;
}> = [
  {
    value: 'active',
    label: 'Em andamento',
    hint: 'Abertas e em atendimento',
  },
  {
    value: 'captured',
    label: 'Capturados',
    hint: 'Guardadas para depois',
  },
  {
    value: 'resolved',
    label: 'Finalizados',
    hint: 'Conversas resolvidas',
  },
  {
    value: 'all',
    label: 'Todas',
    hint: 'Inclui finalizados e capturados',
    separated: true,
  },
];

const countForScope = (counts: ConversationQueueCounts, scope: WorkspaceQueueScope): number => {
  if (scope === 'active')
  {
    return counts.active;
  }

  if (scope === 'captured')
  {
    return counts.captured;
  }

  if (scope === 'resolved')
  {
    return counts.resolved;
  }

  return counts.all;
};

const formatQueueCount = (count: number): string => {
  return count.toLocaleString('pt-BR');
};

const formatQueueUnread = (count: number): string => {
  if (count > 99)
  {
    return '99+';
  }

  return String(count);
};

const queueCountClass = (
  scope: WorkspaceQueueScope,
  count: number,
  isSelected: boolean,
  theme: WorkspaceThemeClasses,
): string => {
  if (count === 0)
  {
    return `text-[12px] font-medium tabular-nums leading-none ${theme.faint}`;
  }

  const pill = 'inline-flex min-w-[1.5rem] items-center justify-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold tabular-nums leading-none';

  if (scope === 'captured')
  {
    return `${pill} bg-amber-100 text-amber-800`;
  }

  if (isSelected)
  {
    return `${pill} bg-white text-blue-700 shadow-sm`;
  }

  return `${pill} ${theme.badge}`;
};

interface WorkspaceQueueFiltersProps {
  theme: WorkspaceThemeClasses;
  queueScope: WorkspaceQueueScope;
  counts: ConversationQueueCounts;
  onQueueScopeChange: (scope: WorkspaceQueueScope) => void;
}

const WorkspaceQueueFilters: React.FC<WorkspaceQueueFiltersProps> = ({
  theme,
  queueScope,
  counts,
  onQueueScopeChange,
}) => {
  const selectedIndex = QUEUE_OPTIONS.findIndex((option) => option.value === queueScope);
  const [focusedIndex, setFocusedIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    if (selectedIndex >= 0)
    {
      setFocusedIndex(selectedIndex);
    }
  }, [selectedIndex]);

  const moveFocus = (nextIndex: number) => {
    setFocusedIndex(nextIndex);
    optionRefs.current[nextIndex]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const lastIndex = QUEUE_OPTIONS.length - 1;

    if (event.key === 'ArrowDown')
    {
      event.preventDefault();
      moveFocus(focusedIndex >= lastIndex ? 0 : focusedIndex + 1);
      return;
    }

    if (event.key === 'ArrowUp')
    {
      event.preventDefault();
      moveFocus(focusedIndex <= 0 ? lastIndex : focusedIndex - 1);
      return;
    }

    if (event.key === 'Home')
    {
      event.preventDefault();
      moveFocus(0);
      return;
    }

    if (event.key === 'End')
    {
      event.preventDefault();
      moveFocus(lastIndex);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label="Fila de atendimento"
      aria-describedby="workspace-queue-hint"
      className="flex flex-col gap-0.5"
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
        {
          setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        }
      }}
    >
      {QUEUE_OPTIONS.map((option, index) => {
        const isSelected = option.value === queueScope;
        const count = countForScope(counts, option.value);
        const showUnread = option.value === 'active' && counts.activeUnread > 0;
        const isQuiet = option.value === 'all' && !isSelected;
        const accessibleName = showUnread
          ? `${option.label}, ${count} conversas, ${counts.activeUnread} não lidas`
          : `${option.label}, ${count} conversas`;

        return (
          <React.Fragment key={option.value}>
            {option.separated && (
              <div className="px-2.5 py-1" aria-hidden="true">
                <div className={`border-t ${theme.border}`} />
              </div>
            )}
            <button
              ref={(node) => {
                optionRefs.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={accessibleName}
              tabIndex={index === focusedIndex ? 0 : -1}
              onClick={() => onQueueScopeChange(option.value)}
              className={`flex min-h-[34px] w-full items-center gap-2 rounded-lg px-2.5 py-1 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 lg:min-h-8 ${
                isSelected
                  ? `${theme.selectedRow} ${theme.text}`
                  : `${isQuiet ? theme.muted : theme.text} hover:bg-gray-100 active:bg-gray-200/80`
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className={`block truncate ${isSelected ? 'text-[13px] font-semibold' : isQuiet ? 'text-xs font-medium' : 'text-[13px] font-medium'}`}>
                  {option.label}
                </span>
                {isSelected && (
                  <span id="workspace-queue-hint" className={`mt-0.5 block truncate text-[11px] font-normal leading-tight ${theme.muted}`} aria-live="polite">
                    {option.hint}
                  </span>
                )}
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                {showUnread && (
                  <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold leading-none text-white">
                    {formatQueueUnread(counts.activeUnread)}
                  </span>
                )}
                <span className="flex w-11 justify-end">
                  <span className={queueCountClass(option.value, count, isSelected, theme)}>
                    {formatQueueCount(count)}
                  </span>
                </span>
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const WorkspaceQueue: React.FC<WorkspaceQueueProps> = ({
  theme,
  conversations,
  selectedId,
  loading,
  accessibleInboxes,
  inboxFilter,
  queueScope,
  queueCounts = EMPTY_QUEUE_COUNTS,
  activeView,
  compact = false,
  onSelect,
  onInboxFilterChange,
  onQueueScopeChange,
  onViewChange,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!filtersOpen)
    {
      return undefined;
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape')
      {
        setFiltersOpen(false);
        filtersButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('keydown', onEscape);
    };
  }, [filtersOpen]);

  const viewFilterActive = activeView.type !== 'all';
  const inboxFilterActive = inboxFilter.kind !== 'all';
  const activeFilterCount = (viewFilterActive ? 1 : 0) + (inboxFilterActive ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const clearFilters = () => {
    onViewChange(createAllView());
    onInboxFilterChange({ kind: 'all' });
  };

  const emptyMessage =
    queueScope === 'resolved'
      ? 'Nenhum atendimento finalizado encontrado.'
      : queueScope === 'captured'
        ? 'Nenhuma conversa capturada encontrada.'
        : queueScope === 'all'
          ? 'Nenhuma conversa encontrada.'
          : 'Nenhuma conversa em andamento.';

  return (
    <aside className={`min-h-0 w-full flex-col lg:flex-shrink-0 ${compact ? 'lg:w-[270px]' : 'lg:w-[320px]'} ${theme.surface} lg:border-r ${theme.border} ${selectedId ? 'hidden lg:flex' : 'flex'}`}>
      <div className={`relative z-30 flex-shrink-0 overflow-visible px-2 pt-3 pb-2 border-b ${theme.border}`}>
        <h2 className={`px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wide ${theme.faint}`}>
          Fila de atendimento
        </h2>

        <WorkspaceQueueFilters
          theme={theme}
          queueScope={queueScope}
          counts={queueCounts}
          onQueueScopeChange={onQueueScopeChange}
        />

        <div className="mt-1">
          <div className="flex min-h-8 items-center gap-2 px-1">
            <button
              ref={filtersButtonRef}
              type="button"
              aria-expanded={filtersOpen}
              aria-controls="workspace-queue-filters"
              onClick={() => setFiltersOpen((open) => !open)}
              className={`inline-flex min-h-8 items-center gap-1 rounded-md bg-transparent px-1.5 text-[11px] font-medium ${theme.muted} transition-colors hover:text-gray-800 focus:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 aria-expanded:bg-transparent`}
            >
              <ChevronDownIcon
                className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-150 motion-reduce:transition-none ${filtersOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
              Filtros
              {hasActiveFilters && (
                <span className="ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold leading-none text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto rounded-md px-1.5 py-1 text-[11px] font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
              >
                Limpar
              </button>
            )}
          </div>

          <div
            className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
              filtersOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
            <div className={filtersOpen ? 'overflow-visible' : 'overflow-hidden'}>
              <div
                id="workspace-queue-filters"
                role="region"
                aria-label="Filtros da fila"
                aria-hidden={!filtersOpen}
                ref={(node) => {
                  if (!node)
                  {
                    return;
                  }

                  if (filtersOpen)
                  {
                    node.removeAttribute('inert');
                    return;
                  }

                  node.setAttribute('inert', '');
                }}
                className={`mt-1 grid grid-cols-1 gap-2 border-t px-0.5 py-2 ${theme.border} ${
                  filtersOpen ? '' : 'pointer-events-none'
                }`}
              >
                <WorkspaceViewSelector
                  theme={theme}
                  activeView={activeView}
                  onViewChange={onViewChange}
                />

                <WorkspaceInboxFilter
                  theme={theme}
                  inboxes={accessibleInboxes}
                  value={inboxFilter}
                  onChange={onInboxFilterChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div
          className="h-0.5 w-full flex-shrink-0 overflow-hidden bg-blue-100"
          role="progressbar"
          aria-label="Carregando fila"
          aria-valuetext="Carregando fila"
        >
          <div className="h-full w-1/3 bg-blue-600 animate-pulse" />
        </div>
      )}

      <div
        className={`relative z-0 min-h-0 flex-1 overflow-y-auto ${loading && conversations.length > 0 ? 'pointer-events-none opacity-60' : ''}`}
        aria-busy={loading}
      >
        {loading && conversations.length === 0 ? (
          <div className={`p-6 text-center text-sm ${theme.muted}`} role="status">
            Carregando a fila…
          </div>
        ) : conversations.length === 0 ? (
          <div className={`p-6 text-center text-sm ${theme.muted}`}>
            {emptyMessage}
          </div>
        ) : (
          conversations.map((conversation) => {
            const isSelected = conversation.id === selectedId;
            const isResolved = conversation.status === ConversationStatus.Resolved;
            const channelLabel = CHANNEL_TYPE_LABELS[conversation.channelType] ?? 'Canal';
            const classificationBadges = resolveClassificationBadges(conversation);
            const unreadCount = conversation.unreadCount ?? 0;
            const hasUnread = unreadCount > 0;
            const preview = formatQueuePreview(conversation.lastNonActivityMessage?.preview);
            const previewMessage = conversation.lastNonActivityMessage;

            return (
              <button
                key={conversation.id}
                type="button"
                onClick={() => onSelect(conversation.id)}
                className={`relative w-full text-left grid grid-cols-[auto_1fr_auto] gap-2.5 px-3.5 py-3 border-b transition-colors ${theme.border} ${
                  isSelected ? theme.selectedRow : 'hover:opacity-90'
                } ${isResolved ? 'opacity-75' : ''}`}
              >
                {isSelected && (
                  <span className={`absolute left-0 top-0 bottom-0 w-0.5 ${theme.selectedBar}`} />
                )}
                {isResolved && !isSelected && (
                  <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-300" />
                )}
                <div
                  className={`h-9 w-9 rounded-full grid place-items-center text-xs font-bold text-white flex-shrink-0 ${
                    isResolved ? 'grayscale' : ''
                  }`}
                  style={{ backgroundColor: avatarColor(conversation.contactName) }}
                >
                  {initialsFromName(conversation.contactName)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className={`text-[13px] truncate ${hasUnread ? 'font-semibold' : 'font-medium'} ${theme.text}`}>
                      {conversation.contactName}
                    </p>
                    {isResolved && (
                      <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                        Finalizado
                      </span>
                    )}
                    {classificationBadges.length > 0 && (
                      <ClassificationBadges badges={classificationBadges} compact />
                    )}
                  </div>
                  <p className={`text-xs truncate mt-0.5 flex items-center gap-1 ${
                    hasUnread ? `font-medium ${theme.text}` : theme.muted
                  }`}>
                    {previewMessage?.isPrivate && (
                      <LockClosedIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    )}
                    {!previewMessage?.isPrivate && previewMessage?.messageType === SupportMessageType.Outgoing && (
                      <ArrowUturnLeftIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    )}
                    <span className="truncate">{preview}</span>
                  </p>
                  {!compact && (
                    <p className={`text-[11px] truncate mt-0.5 ${theme.faint}`}>
                      #{conversation.displayId} · {conversation.inboxName}
                      {conversation.supportGroupName ? ` · ${conversation.supportGroupName}` : ''}
                    </p>
                  )}
                  {!compact && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] ${theme.faint}`}>
                        <ChannelTypeIcon channelType={conversation.channelType} className="h-3.5 w-3.5" />
                        {channelLabel}
                      </span>
                      {!isResolved && (
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${statusTone(conversation.status)}`}>
                          {CONVERSATION_STATUS_LABELS[conversation.status]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`text-[11px] whitespace-nowrap ${theme.faint}`}>
                    {formatRelativeTime(conversation.lastActivityAt)}
                  </span>
                  {hasUnread && (
                    <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold grid place-items-center">
                      {formatUnreadBadge(unreadCount)}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
};
